import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import {
  ListView,
  MapView,
  SearchFilter,
  SearchText,
  MapLegend
} from './components';
import {
  DetailsPanel,
  EnquiryDetails,
  EnquiryLimitReached,
  EnquiryDetailsExpired
} from 'components';
import {
  Button,
  ContentSwitcher,
  Switch,
  Toggle
} from 'carbon-components-react';
import { selectScreenSize } from 'redux/session/selectors';
import { useSelector } from 'react-redux';
import { SCREEN_SIZE } from 'types';
import { ListBulleted16, Map16 } from '@carbon/icons-react';
import { APP } from 'app/AppRoutes';
import { selectCurrentTeamId } from 'redux/team/selectors';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import { useComponentMount } from 'utilities/hooks.util';
import { getCredentialStart } from './localStore/actions';

export const Home: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ enquiryId?: string; jobId?: string }>();
  const overallContainerRef = useRef(null);
  const clusterVisible = useRef<boolean>(true);
  const screenSize = useSelector(selectScreenSize);
  const currentTeamId = useSelector(selectCurrentTeamId);
  const [isListView, setIsListView] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEnquiryDataRotated, setIsEnquiryDataRotated] =
    useState<boolean>(false);
  const [isEnquiryLimitedReached, setIsEnquiryLimitedReached] =
    useState<boolean>(false);
  const [enquiryIdDetails, setEnquiryIdDetails] = useState<any>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [defaultSearchText, setDefaultSearchText] = useState<string>('');
  const [selectedRow, setSelectedRow] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<object>({});
  const [showMyEnquiriesOnly, setShowMyEnquiriesOnly] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [refreshMap, setRefreshMap] = useState<boolean>(false);
  const [showSentinelLoginIframe, setShowSentinelLoginIframe] =
    useState<boolean>(true);
  const [sentinelSuccesfulLogin, setSentinelSuccesfulLogin] =
    useState<boolean>(false);

  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { state, credentials } = localStore;
  const { username, token } = credentials || {};

  useComponentMount(() => {
    process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true' &&
      localDispatch(getCredentialStart());
  });

  const handleSetEnquiryId = (
    enquiryId: any,
    isDataRotated = false,
    isLimitedReached = false
  ) => {
    setEnquiryIdDetails(Number(enquiryId));
    setIsEnquiryDataRotated(isDataRotated);
    setIsEnquiryLimitedReached(isLimitedReached);
    setSelectedRow(enquiryId);
  };

  const handleCloseIconClick = (refresh: boolean = false) => {
    setEnquiryIdDetails(0);
    setIsModalOpen(false);
    setSelectedRow('');

    if (refresh) {
      refreshSearch();
    }
  };

  const refreshSearch = useCallback(() => {
    setSearchFilter({
      ...searchFilter
    });
  }, [searchFilter]);

  const onOffClusterLayer = () => {
    const { loadedMap, loadedView } = window;
    if (loadedMap) {
      const featureLayer = loadedMap.findLayerById('enquiries-feature-layer');
      const clusterLayer = loadedMap.findLayerById('enquiries-cluster-layer');
      if (loadedView.zoom > 13) {
        if (clusterLayer) {
          clusterLayer.visible = false;
        }
        if (featureLayer) {
          featureLayer.visible = true;
        }
      } else {
        if (clusterLayer) {
          clusterLayer.visible = clusterVisible.current;
        }
        if (featureLayer) {
          featureLayer.visible = !clusterVisible.current;
        }
      }
    }
  };

  const mapStyleClusterHandler = (flag: boolean) => {
    clusterVisible.current = flag;
    onOffClusterLayer();
  };

  useEffect(() => {
    if (enquiryIdDetails !== 0) {
      setIsModalOpen(true);
    }
  }, [enquiryIdDetails]);

  useEffect(() => {
    if (location.state?.enquiryId) {
      handleSetEnquiryId(location.state.enquiryId);
      if (location.state?.jobId) {
        setIsListView(true);
        setSearchText(location.state.jobId);
        setDefaultSearchText(location.state.jobId);
      }
      history.replace({ state: undefined });
    }
  }, [location, history]);

  useEffect(() => {
    if (refreshMap) {
      setRefreshMap(false);
    }
  }, [refreshMap]);

  useEffect(() => {
    if (state === 'success' && sentinelSuccesfulLogin) {
      setTimeout(() => {
        setShowSentinelLoginIframe(false);
      }, 5000);
    }
  }, [state, sentinelSuccesfulLogin]);

  window.addEventListener(
    'message',
    (event) => {
      const trustedOrigin = process.env.REACT_APP_SENTINEL_WEB_URL;
      if (event.origin !== trustedOrigin) return;

      if (event.data === 'External app logged in successfully') {
        setSentinelSuccesfulLogin(true);
      }
    },
    false
  );

  return (
    <div id="Home" data-testid="Home" className="home">
      {state === 'success' && showSentinelLoginIframe && (
        <iframe
          title="Sentinel Login"
          src={`${process.env.REACT_APP_SENTINEL_WEB_URL}/external/auth?username=${username}&token=${token}`}
          className="sentinel-login-iframe"
        />
      )}
      {isModalOpen &&
        !isEnquiryLimitedReached &&
        process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION !== 'true' &&
        ReactDOM.createPortal(
          <DetailsPanel
            handleCloseIconClick={handleCloseIconClick}
            enquiryIdDetails={enquiryIdDetails}
            setEnquiryIdDetails={setEnquiryIdDetails}
            isListView={isListView}
            setIsListView={setIsListView}
            setSearchText={setSearchText}
            setSearchInputValue={setSearchInputValue}
            refreshMap={() => setRefreshMap(true)}
          />,
          document.querySelector(`#${APP}`) || document.body
        )}
      {isModalOpen &&
        !isEnquiryDataRotated &&
        !isEnquiryLimitedReached &&
        process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true' &&
        ReactDOM.createPortal(
          <EnquiryDetails
            handleCloseIconClick={handleCloseIconClick}
            enquiryIdDetails={enquiryIdDetails}
            setEnquiryIdDetails={setEnquiryIdDetails}
            isListView={isListView}
            setIsListView={setIsListView}
            setSearchText={setSearchText}
            setSearchInputValue={setSearchInputValue}
            sentinelSuccesfulLogin={sentinelSuccesfulLogin}
            refreshMap={() => setRefreshMap(true)}
          />,
          document.querySelector(`#${APP}`) || document.body
        )}
      {isModalOpen &&
        isEnquiryDataRotated &&
        !isEnquiryLimitedReached &&
        process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true' &&
        ReactDOM.createPortal(
          <EnquiryDetailsExpired
            handleCloseIconClick={handleCloseIconClick}
            enquiryIdDetails={enquiryIdDetails}
          />,
          document.querySelector(`#${APP}`) || document.body
        )}
      {isModalOpen &&
        isEnquiryLimitedReached &&
        ReactDOM.createPortal(
          <EnquiryLimitReached handleCloseIconClick={handleCloseIconClick} />,
          document.querySelector(`#${APP}`) || document.body
        )}
      <FullHeaderLeftSideFullPanelLayout
        enableHeaderGlobalBar={true}
        mapStyleClusterHandler={mapStyleClusterHandler}
        mapStyleClusterVisibleRef={clusterVisible}
        mapStyleShowMasking={true}
        mapStyleShowLegend={<MapLegend />}
      >
        <section className={`home--header`}>
          <div className="page--filter-search">
            <div>
              <SearchFilter
                mainContainerRef={overallContainerRef}
                setSearchFilter={setSearchFilter}
                showMyEnquiriesOnly={showMyEnquiriesOnly}
                currentTeamId={currentTeamId}
                setCurrentPage={setCurrentPage}
                setPageOffset={setPageOffset}
              />
            </div>
            <div className="job-id-search-container">
              <SearchText
                setSearchText={setSearchText}
                setSearchInputValue={setSearchInputValue}
                searchInputValue={searchInputValue}
                defaultSearchText={defaultSearchText}
                setCurrentPage={setCurrentPage}
                setPageOffset={setPageOffset}
                searchFilter={searchFilter}
              />
            </div>
          </div>
          <div className="page--controls">
            <div className="toggle">
              <Toggle
                id={'toggleActionButton'}
                aria-label="My enquiries"
                labelA="My enquiries"
                labelB="My enquiries"
                onToggle={(showMyEnquiriesOnly) =>
                  setShowMyEnquiriesOnly(!!showMyEnquiriesOnly)
                }
              />
            </div>
            <div className="page--switcher">
              {screenSize === SCREEN_SIZE.SM ||
              screenSize === SCREEN_SIZE.MD ? (
                <>
                  <Button
                    size="lg"
                    className={`icon-button-switcher ${
                      !isListView ? 'icon-button-switcher--selected' : ''
                    }`}
                    hasIconOnly
                    renderIcon={Map16}
                    tooltipAlignment="center"
                    tooltipPosition="bottom"
                    iconDescription="Map view"
                    onClick={() => setIsListView(false)}
                  />
                  <Button
                    size="lg"
                    className={`icon-button-switcher ${
                      isListView ? 'icon-button-switcher--selected' : ''
                    }`}
                    hasIconOnly
                    renderIcon={ListBulleted16}
                    tooltipAlignment="center"
                    tooltipPosition="bottom"
                    iconDescription="List view"
                    onClick={() => setIsListView(true)}
                  />
                </>
              ) : (
                <ContentSwitcher selectedIndex={isListView ? 1 : 0}>
                  <Switch
                    name={'map-view'}
                    text="Map view"
                    onClick={() => setIsListView(false)}
                  />
                  <Switch
                    name={'list-view'}
                    text="List view"
                    onClick={() => setIsListView(true)}
                  />
                </ContentSwitcher>
              )}
            </div>
          </div>
        </section>
        <section className="home--view-switcher">
          {isListView ? (
            <ListView
              handleSetEnquiryId={handleSetEnquiryId}
              isListView={isListView}
              searchText={searchText}
              selectedRow={selectedRow}
              searchFilter={searchFilter}
              showMyEnquiriesOnly={showMyEnquiriesOnly}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageOffset={pageOffset}
              setPageOffset={setPageOffset}
            />
          ) : (
            <MapView
              refreshMap={refreshMap}
              handleSetEnquiryId={handleSetEnquiryId}
              searchFilter={searchFilter}
              searchText={searchText}
              selectedId={selectedRow}
              onOffClusterLayer={onOffClusterLayer}
              showMyEnquiriesOnly={showMyEnquiriesOnly}
            />
          )}
        </section>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

export default React.memo(Home);
